import React, { useCallback, useEffect, useRef, useState } from "react";
import type { AddressSearchProps, GroupSuggestion, DetailSuggestion } from "./index";
import { defaultFormatSelected } from "./utils/format";
import { handleListKey } from "./utils/keyboard";

type Level = "groups" | "details";

type HookArgs = Pick<
  AddressSearchProps,
  | "value" | "onChange" | "onClear"
  | "fetchGroupSuggestions" | "fetchDetailSuggestions"
  | "onSelectGroup" | "onSelectDetail" | "onEnterManual"
  | "debounceMs" | "minChars" | "keepPostcodeOnSelect"
  | "showSummaryCard" | "manualValue" | "onManualChange"
  | "formatSelectedLabel" | "onFormattedSelect"
>;

/**
 * Address autocomplete state + behaviors.
 *
 * Manages query debouncing, fetching group/detail suggestions, keyboard navigation,
 * manual-entry mode, and (optionally) formatting the selected address text.
 *
 * @param value Current text in the search input (controlled).
 * @param onChange Called when the search input changes.
 * @param onClear Called when the clear action is triggered.
 * @param fetchGroupSuggestions Fetches “group” suggestions (e.g., postcode areas) for a query.
 * @param fetchDetailSuggestions Fetches detailed address suggestions for a selected group id.
 * @param onSelectGroup Optional callback when a group row is chosen.
 * @param onSelectDetail Required callback when a detail row is chosen.
 * @param onEnterManual Optional callback when the user opts for manual entry.
 * @param debounceMs Debounce for querying groups (ms). Default 300.
 * @param minChars Minimum normalized characters before querying. Default 2.
 * @param keepPostcodeOnSelect If true, keeps the group code (e.g., postcode) in the input. Default true.
 * @param showSummaryCard If true, renders the legacy internal textarea summary. Default false.
 * @param manualValue Optional controlled value for the internal textarea (legacy).
 * @param onManualChange Optional controlled setter for the internal textarea (legacy).
 * @param formatSelectedLabel Optional formatter for turning a detail into multiline text.
 * @param onFormattedSelect Optional callback with the formatted text after selecting a detail.
 * @returns State and event handlers used by the AddressSearch component.
 */
export function useAddressSearch({
  value,
  onChange,
  onClear,
  fetchGroupSuggestions,
  fetchDetailSuggestions,
  onSelectGroup,
  onSelectDetail,
  onEnterManual,
  debounceMs = 300,
  minChars = 2,
  keepPostcodeOnSelect = true,
  showSummaryCard = false,
  manualValue,
  onManualChange,
  formatSelectedLabel,
  onFormattedSelect
}: HookArgs) {
  // UI state
  const [open, setOpen] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [level, setLevel] = useState<Level>("groups");
  const [groups, setGroups] = useState<GroupSuggestion[]>([]);
  const [details, setDetails] = useState<DetailSuggestion[]>([]);
  const [highlight, setHighlight] = useState(-1);

  const [selectedAddress, setSelectedAddress] = useState<string | null>(null);
  const [manualMode, setManualMode] = useState<boolean>(false);
  const [manualText, setManualText] = useState("");
  const manualRef = useRef<HTMLTextAreaElement | null>(null);
  const [showSummary, setShowSummary] = useState<boolean>(false);

  const wrapRef = useRef<HTMLDivElement | null>(null);
  const suppressNextQueryRef = useRef<boolean>(false);

  const queryIdRef = useRef(0);
  const levelRef = useRef<Level>("groups");
  const manualModeRef = useRef<boolean>(false);
  const lastGroupCodeRef = useRef<string | null>(null);

  const normalize = (s: string) => (s ?? "").replace(/\s+/g, "").toUpperCase();

  useEffect(() => { levelRef.current = level; }, [level]);
  useEffect(() => { manualModeRef.current = manualMode; }, [manualMode]);

  const hasValue = (value ?? "").length > 0;
  const isQuerying = !manualMode && normalize((value ?? "").trim()).length >= minChars && loading;

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  useEffect(() => {
    const rawQuery = (value ?? "").trim();
    const normaliseQuery = normalize(rawQuery);

    if (manualMode) { setOpen(false); return; }
    if (level === "details") return;

    if (suppressNextQueryRef.current) { suppressNextQueryRef.current = false; return; }

    if (normaliseQuery.length < minChars) {
      setGroups([]); setDetails([]); setLevel("groups");
      setOpen(false); setLoading(false); setHighlight(-1);
      return;
    }

    const requestId = ++queryIdRef.current;
    setLoading(true);

   const run = async () => {
      try {
        let fetched = await fetchGroupSuggestions(normaliseQuery);
        let results = Array.isArray(fetched) ? fetched : [];

        if ((!results || results.length === 0) && rawQuery !== normaliseQuery) {
          fetched = await fetchGroupSuggestions(rawQuery);
          results = Array.isArray(fetched) ? fetched : [];
        }

        if (queryIdRef.current !== requestId) return;
        if (manualModeRef.current) return;
        if (levelRef.current === "details") return;

        setGroups(results);
        setLevel("groups");
        const shouldOpen = (results?.length ?? 0) > 0 || Boolean(onEnterManual);
        setOpen(shouldOpen);
        setHighlight(results.length ? 0 : -1);
      } catch {
        if (queryIdRef.current === requestId) {
          setGroups([]);
          setOpen(false);
          setHighlight(-1);
        }
      } finally {
        if (queryIdRef.current === requestId) setLoading(false);
      }
    };

    if (debounceMs <= 0) {
    void run();
    return;
  }

   const t = setTimeout(run, debounceMs);
   return () => clearTimeout(t);
  }, [value, manualMode, level, debounceMs, minChars, fetchGroupSuggestions, onEnterManual]);

  const handleClear = useCallback(() => {
    onClear();
    setOpen(false);
    setGroups([]); setDetails([]); setLevel("groups");
    setHighlight(-1);
    setManualMode(false);
    setSelectedAddress(null);
    onManualChange?.("");
    setManualText("");
    setShowSummary(false);
    lastGroupCodeRef.current = null;
    suppressNextQueryRef.current = false;
  }, [onClear, onManualChange]);

  const drillInto = useCallback(async (group: GroupSuggestion) => {
    setLoading(true);
    lastGroupCodeRef.current = group.code;
    onSelectGroup?.(group);
    const res = await fetchDetailSuggestions(group.id);
    setDetails(res);
    setLevel("details");
    setOpen(true);
    setLoading(false);
    setHighlight(res.length ? 0 : -1);
  }, [fetchDetailSuggestions, onSelectGroup]);

  const selectDetail = useCallback((detail: DetailSuggestion) => {
    suppressNextQueryRef.current = true;
    setOpen(false);
    setGroups([]); setDetails([]); setLevel("groups");
    setHighlight(-1);
    setManualMode(true);
    setShowSummary(true);

    if (keepPostcodeOnSelect && lastGroupCodeRef.current) {
      onChange(lastGroupCodeRef.current);
    }

    onSelectDetail(detail);

    const fullText = (formatSelectedLabel ?? ((_, detail) => defaultFormatSelected(detail)))(detail.label, detail);
    onFormattedSelect?.(fullText, detail);

    if (showSummaryCard) {
      setSelectedAddress(fullText);
      if (onManualChange) onManualChange(fullText);
      else setManualText(fullText);
      requestAnimationFrame(() => manualRef.current?.focus());
    }
  }, [keepPostcodeOnSelect, onChange, onSelectDetail, showSummaryCard, onManualChange, formatSelectedLabel, onFormattedSelect]);

  const enterManual = useCallback(() => {
    onEnterManual?.();
    setSelectedAddress("");
    setManualText("");
    setManualMode(true);
    setShowSummary(true);
    setOpen(false);
    setGroups([]); setDetails([]); setLevel("groups");
    setHighlight(-1);
    requestAnimationFrame(() => manualRef.current?.focus());
  }, [onEnterManual]);

    const handleInputChange = useCallback((next: string) => {
    setManualMode(false);
    setSelectedAddress(null);
    suppressNextQueryRef.current = false;

    setLevel("groups");
    setDetails([]);
    setHighlight(-1);
    lastGroupCodeRef.current = null;

    if (normalize(next).length < minChars) {
        setOpen(false);
        setGroups([]);
    }

    onChange(next);
    }, [onChange, minChars]);

    const onKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLInputElement>) => {
      handleListKey<GroupSuggestion, DetailSuggestion>(evt, {
        manualMode, open, level, groups, details, highlight,
        setHighlight, setOpen, setLevel, setDetails,
        drillInto, selectDetail,
      });
    },
    [manualMode, open, level, groups, details, highlight, drillInto, selectDetail]
  );

  const manualValueToShow = manualValue ?? (manualText || selectedAddress || "");

/**
 * Updates manual textarea value and ensures the dropdown stays closed while typing manually.
 * Resets list state so next top-input keystroke can re-open suggestions.
 */
  const handleManualTextareaChange = useCallback(
    (evt: React.ChangeEvent<HTMLTextAreaElement>) => {
      const nextValue = evt.target.value;
      setOpen(false);
      setGroups([]);
      setDetails([]);
      setLevel("groups");
      setHighlight(-1);
      suppressNextQueryRef.current = false;
      (onManualChange ?? setManualText)(nextValue);
    },
    [onManualChange]
  );

/**
 * Prevents accidental form submit on Enter (single-line Enter) and exits manual mode on Escape.
 */
  const handleManualTextareaKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (evt.key === "Enter" && !evt.shiftKey) {
        evt.stopPropagation();
        return;
      }
      if (evt.key === "Escape") {
        evt.preventDefault();
        setManualMode(false);
        setSelectedAddress(null);
      }
    },
    [setManualMode, setSelectedAddress]
  );

  return {
    open, loading, level, groups, details, highlight,
    manualMode, selectedAddress, showSummary,
    wrapRef, manualRef,
    hasValue, isQuerying, manualValueToShow,
    setHighlight,
    handleClear,
    handleInputChange,
    onKeyDown,
    drillInto,
    selectDetail,
    enterManual,
    handleManualTextareaChange,
    handleManualTextareaKeyDown,
  };
}